import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Alert, Select, Tabs } from 'antd';
import {
  ContentBackground,
  ContentWrapper,
  ContentHeader,
  ContentBody,
} from '@/components/content';
import { withPrivateRoute } from '@/components/hoc/withPrivateRoute';
import { ContainerFluid, Section } from '@/components/layouts';
import RootLayout from '@/components/layouts/RootLayout';
import { ROLES_PERMISSIONS } from '@/constants';
import { API } from '@/services/api.service';
import MappingsPanel from './_components/MappingsPanel';
import PackagePanel from './_components/PackagePanel';
import RunWizardPanel from './_components/RunWizardPanel';
import RunsListPanel from './_components/RunsListPanel';

/**
 * Group reporting: turn the local ledger into the parent company's monthly pack.
 *
 * @returns {JSX.Element} The page
 */
function GroupReportingContent() {
  const { t } = useTranslation(['tables', 'menus', 'forms', 'messages']);
  const gr = (key) => t(`groupReporting.${key}`, { ns: 'tables' });

  const [activeTab, setActiveTab] = useState('newRun');
  const [packageId, setPackageId] = useState(null);

  const { data: packagesData, isLoading } = useQuery({
    queryKey: ['group-reporting-packages'],
    queryFn: () => API.groupReporting.getPackages({ isActive: true }),
  });

  const packages = packagesData?.data || [];
  const selectedPackage =
    packages.find((item) => item._id === packageId) || null;

  useEffect(() => {
    if (!packageId && packages.length) setPackageId(packages[0]._id);
  }, [packages, packageId]);

  const tabItems = [
    {
      key: 'newRun',
      label: gr('newRun'),
      children: <RunWizardPanel packages={packages} />,
    },
    {
      key: 'runs',
      label: gr('runs'),
      children: <RunsListPanel packageId={packageId} />,
    },
    {
      key: 'mappings',
      label: gr('mappings'),
      children: <MappingsPanel packageId={packageId} />,
    },
    {
      key: 'package',
      label: gr('packageTab'),
      children: <PackagePanel reportingPackage={selectedPackage} />,
    },
  ];

  return (
    <RootLayout metadata={{ title: t('groupReporting', { ns: 'menus' }) }}>
      <ContentBackground source="/assets/images/bg-pattern.svg" />

      <Section>
        <ContainerFluid className="foreground-wrapper">
          <ContentWrapper>
            <ContentHeader title={t('groupReporting', { ns: 'menus' })} />

            <ContentBody>
              <div className="with-y-overflow">
                <div className="p-4">
                  <p className="mb-4 text-sm opacity-70">{gr('subtitle')}</p>

                  {!isLoading && !packages.length && (
                    <Alert
                      type="info"
                      showIcon
                      message={gr('noPackages')}
                      style={{ marginBottom: 16 }}
                    />
                  )}

                  {packages.length > 1 && (
                    <Select
                      style={{ maxWidth: 420, marginBottom: 12 }}
                      value={packageId}
                      onChange={setPackageId}
                      options={packages.map((item) => ({
                        value: item._id,
                        label: `${item.code} — ${item.name}`,
                      }))}
                    />
                  )}

                  <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={tabItems}
                  />
                </div>
              </div>
            </ContentBody>
          </ContentWrapper>
        </ContainerFluid>
      </Section>
    </RootLayout>
  );
}

const GroupReportingPageWithAuth = withPrivateRoute(
  GroupReportingContent,
  ROLES_PERMISSIONS.GROUP_REPORTING.VIEW
);
GroupReportingPageWithAuth.displayName = 'GroupReportingPage';

export default GroupReportingPageWithAuth;
